import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Mic, MicOff, CheckCircle2, Brain, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Conversation } from '@11labs/client';
import { motion } from 'framer-motion';
import { PracticeData } from '@/pages/InterviewPractice';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface PracticeInterviewProps {
  practiceData: PracticeData;
  practiceId: string | null;
}

const useTypewriter = (text: string, speed: number = 30) => {
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  
  useEffect(() => {
    if (!text) {
      setDisplayText('');
      return;
    }
    
    setIsTyping(true);
    setDisplayText(''); // Reset display text
    
    let index = 0;
    
    const intervalId = setInterval(() => {
      if (index < text.length) {
        setDisplayText(text.substring(0, index + 1));
        index++;
      } else {
        setIsTyping(false);
        clearInterval(intervalId);
      }
    }, speed);
    
    return () => clearInterval(intervalId);
  }, [text, speed]);
  
  return { displayText, isTyping };
};

const PracticeInterview: React.FC<PracticeInterviewProps> = ({ practiceData, practiceId }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Array<{ source: string; message: string }>>([]);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [transcriptText, setTranscriptText] = useState('');
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [isEnded, setIsEnded] = useState(false);
  const [practiceCompleted, setPracticeCompleted] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isDownloadingAudio, setIsDownloadingAudio] = useState(false);
  
  const conversationRef = useRef<any>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const cleanupRef = useRef(false);
  const retryCountRef = useRef(0);
  const monitorIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const MAX_RETRIES = 3;

  // Slow down typing speed for better readability
  const { displayText: animatedQuestion, isTyping: isQuestionTyping } = useTypewriter(currentQuestion || '', 40);

  // Initialize microphone
  const initializeMicrophone = async (stream: MediaStream) => {
    try {
      // Ensure all audio tracks are initially enabled
      const audioTracks = stream.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = true;
      });
      
      // Set UI state to match (unmuted)
      setIsMuted(false);
    } catch (error) {
      // Handle error silently
      console.error('Error initializing microphone:', error);
    }
  };

  // Start the interview
  const startInterview = useCallback(async () => {
    try {
      if (isConnecting) {
        return;
      }

      // Request microphone permission
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (!stream) {
          toast.error("Microphone permission is required for the interview practice.");
          return;
        }
        audioStreamRef.current = stream;
        
        // Initialize microphone (ensure it's unmuted at the start)
        await initializeMicrophone(stream);
      } catch (error) {
        toast.error("Microphone permission is required for the interview practice.");
        return;
      }

      setIsConnecting(true);

      // Use environment variable for agent ID with fallback
      const agentId = import.meta.env.VITE_PRACTICE_INTERVIEW_AGENT_ID || "GCZ2Fc3iMWvVmSjOVc8w";

      // Start the conversation session
      const conversation = await Conversation.startSession({
        agentId: agentId,
        dynamicVariables: {
          candidate_name: practiceData.candidateName || user?.user_metadata?.name || "Candidate",
          company_name: "RoundOne",
          role_name: practiceData.role,
          job_role: practiceData.role,
          skills: practiceData.selectedSkills.join(', '),
          custom_questions: ""
        },
        onConnect: () => {
          setIsConnecting(false);
          setHasStarted(true);
          retryCountRef.current = 0;
          toast.success('Connected to interviewer');
        },
        onDisconnect: () => {
          // If we're already handling cleanup, don't do anything
          if (cleanupRef.current) {
            return;
          }

          setIsConnecting(false);
          
          // Only set hasStarted to false if we're not completing the interview
          if (!practiceCompleted) {
            setHasStarted(false);
          }
          
          setIsSpeaking(false);

          // Check if this was a natural end to the conversation
          const lastMessage = messages[messages.length - 1];
          const isNaturalEnd = lastMessage?.source === 'ai' && (
            lastMessage.message.toLowerCase().includes('thank you') ||
            lastMessage.message.toLowerCase().includes('best of luck') ||
            lastMessage.message.toLowerCase().includes('wish you') ||
            lastMessage.message.toLowerCase().includes('end this call')
          );

          if (isNaturalEnd) {
            // Handle natural end
            setIsEnded(true);
            setPracticeCompleted(true);
            return;
          }

          // If not a natural end and we haven't exceeded retries, attempt reconnection
          if (hasStarted && retryCountRef.current < MAX_RETRIES) {
            retryCountRef.current += 1;
            
            setTimeout(() => {
              if (!cleanupRef.current && !isConnecting) {
                startInterview().catch(error => {
                  toast.error('Failed to reconnect. Please try again.');
                });
              }
            }, 2000);
          } else if (retryCountRef.current >= MAX_RETRIES) {
            toast.error('Connection lost. Please refresh the page to try again.');
          }
        },
        onError: (error) => {
          toast.error('An error occurred during the interview.');
          setIsConnecting(false);
          setHasStarted(false);
        },
        onMessage: (props) => {
          // Only process if we have a valid message
          if (!props.message) return;
          
          // Set speaking state based on message source
          setIsSpeaking(props.source === "ai");
          
          // Format message with candidate name
          const candidateName = user ? user.user_metadata?.name || "You" : "You";
          const newMessage = {
            contentType: props.source === "ai" ? "Q" : "A",
            operator: "AI Interviewer",
            userName: props.source === "user" ? candidateName : "AI Interviewer",
            content: props.message,
            qaTime: Date.now(),
          };

          // Update messages state with source being either "ai" or the candidate's name
          setMessages(prevMessages => [...prevMessages, { 
            source: props.source === "user" ? "You" : "ai", 
            message: props.message 
          }]);

          // Update transcript with delay for AI messages
          if (props.source === "ai") {
            // Clean the message to remove any undefined or unexpected content
            let cleanedMessage = String(props.message);
            cleanedMessage = cleanedMessage.replace(/undefined$/, '');
            setCurrentQuestion(cleanedMessage);
            
            // Add delay for AI messages for smoother UI updates
            setTimeout(() => {
              setTranscriptText(prev => prev + `${newMessage.userName}: ${cleanedMessage}\n`);
              // Scroll to bottom after AI message
              const transcriptElement = document.querySelector('.transcript-container');
              if (transcriptElement) {
                transcriptElement.scrollTop = transcriptElement.scrollHeight;
              }
            }, 1000);
          } else {
            // Update immediately for user messages
            setTranscriptText(prev => prev + `${newMessage.userName}: ${props.message}\n`);
            // Scroll to bottom immediately for user messages
            setTimeout(() => {
              const transcriptElement = document.querySelector('.transcript-container');
              if (transcriptElement) {
                transcriptElement.scrollTop = transcriptElement.scrollHeight;
              }
            }, 100);
          }
        },
        onModeChange: ({ mode }) => {
          setIsSpeaking(mode === "speaking");
        }
      });

      conversationRef.current = conversation;
      
      // Try to get conversation ID after conversation is created
      if (conversation) {
        // Get the conversation ID from the connection object
        const convId = (conversation as any)?.connection?.conversationId;
        if (convId) {
          setConversationId(convId);
        }
      }
    } catch (error) {
      toast.error('Failed to start conversation. Please try again.');
      setIsConnecting(false);
      setHasStarted(false);
    }
  }, [isConnecting, hasStarted, user, practiceData, practiceCompleted, messages]);

  // Stop connection monitor
  const stopConnectionMonitor = useCallback(() => {
    if (monitorIntervalRef.current) {
      clearInterval(monitorIntervalRef.current);
      monitorIntervalRef.current = null;
    }
  }, []);

  // Connection monitor
  useEffect(() => {
    if (hasStarted && !cleanupRef.current) {
      monitorIntervalRef.current = setInterval(() => {
        // Monitor connection
      }, 5000);
    }

    return () => {
      stopConnectionMonitor();
    };
  }, [hasStarted, stopConnectionMonitor]);

  // End the interview
  const endInterview = async () => {
    if (cleanupRef.current) {
      return;
    }
    
    try {
      cleanupRef.current = true;
      setIsEnded(true);
      stopConnectionMonitor();
      
      // First, stop all audio tracks to immediately stop any interaction
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => {
          track.stop();
        });
        audioStreamRef.current = null; // Clear the reference
      }
      
      // End the conversation session
      if (conversationRef.current) {
        try {
          await conversationRef.current.endSession();
        } catch (endSessionError) {
          console.error('Error ending session:', endSessionError);
        } finally {
          // Clear the conversation reference to prevent further interactions
          conversationRef.current = null;
        }
      }

      // Get conversation ID from the state (grab it now and don't use ref)
      const currentConversationId = conversationId;

      // If user is logged in, save the practice result
      if (user && practiceId) {
        try {
          await supabase
            .from('practice_interviews')
            .update({
              transcript: messages,
              completed_at: new Date().toISOString(),
              conversation_id: currentConversationId
            })
            .eq('id', practiceId);
        } catch (error) {
          console.error('Error saving practice result:', error);
        }
      } else if (!user && !showLoginPrompt) {
        // If user is not logged in, try to save anonymous interview results
        try {
          const { data, error } = await supabase
            .from('practice_interviews')
            .insert([
              {
                role: practiceData.role,
                level: practiceData.level,
                skills: practiceData.selectedSkills,
                candidate_name: practiceData.candidateName,
                candidate_email: practiceData.candidateEmail,
                transcript: messages,
                completed_at: new Date().toISOString(),
                created_at: new Date().toISOString(),
                conversation_id: currentConversationId
              }
            ])
            .select()
            .single();
            
          if (error && error.code !== '23505') { // Ignore unique constraint violations
            console.error('Error saving anonymous interview:', error);
          }
        } catch (error) {
          console.error('Error saving anonymous interview:', error);
        }
        
        // Show login prompt anyway in case they want to properly register
        setShowLoginPrompt(true);
      }
      
      // Small delay to ensure conversation is fully ended
      setTimeout(() => {
        // If we have a conversation ID, fetch the audio
        if (currentConversationId) {
          fetchConversationAudio();
        }
        
        setPracticeCompleted(true);
      }, 1000);
    } catch (error) {
      console.error('Error ending interview:', error);
    }
  };

  // Toggle mute
  const toggleMute = async () => {
    if (!hasStarted || !audioStreamRef.current) return;
    
    try {
      const audioTracks = audioStreamRef.current.getAudioTracks();
      if (audioTracks.length > 0) {
        const isCurrentlyMuted = !audioTracks[0].enabled;
        
        // Toggle mute state
        audioTracks.forEach(track => {
          track.enabled = isCurrentlyMuted;
        });
        
        setIsMuted(!isCurrentlyMuted);
        
        toast.success(isCurrentlyMuted ? 'Microphone unmuted' : 'Microphone muted');
      }
    } catch (error) {
      console.error('Error toggling mute:', error);
      toast.error('Failed to toggle microphone');
    }
  };

  // Before unload handler
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Cleanup audio and connection if page is closed/refreshed
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => {
          track.stop();
        });
      }
      
      if (conversationRef.current) {
        conversationRef.current.endSession().catch(console.error);
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      handleBeforeUnload();
    };
  }, []);

  // Login and save
  const handleLoginAndSave = () => {
    navigate('/login', { 
      state: { 
        redirectTo: '/practice-history',
        practiceData: {
          role: practiceData.role,
          level: practiceData.level,
          skills: practiceData.selectedSkills,
          transcript: messages,
          date: new Date().toISOString()
        }
      } 
    });
  };

  // Fetch conversation audio
  const fetchConversationAudio = async () => {
    if (!conversationId) {
      toast.error('Conversation ID not found. Cannot download audio.');
      return;
    }

    try {
      setIsDownloadingAudio(true);
      
      // Call the new Supabase function to get the audio
      const { data, error } = await supabase.functions.invoke('get-conversation-audio', {
        body: { conversationId }
      });

      if (error) {
        console.error('Error from Supabase function:', error);
        throw new Error(error.message || 'Failed to fetch audio');
      }

      if (!data || !data.audioBase64) {
        throw new Error('No audio data received');
      }
      
      // Convert base64 to Blob
      const byteCharacters = atob(data.audioBase64);
      const byteArrays = [];
      
      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);
        
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
        
        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }
      
      const audioBlob = new Blob(byteArrays, { type: 'audio/mpeg' });
      
      // Create an object URL for the blob
      const url = URL.createObjectURL(audioBlob);
      setAudioUrl(url);
      
      toast.success('Audio retrieved successfully');
    } catch (error) {
      console.error('Error fetching conversation audio:', error);
      let errorMessage = 'Failed to retrieve audio';
      if (error instanceof Error) {
        errorMessage += ': ' + error.message;
      }
      toast.error(errorMessage);
    } finally {
      setIsDownloadingAudio(false);
    }
  };

  // Download transcript as text file
  const downloadTranscript = () => {
    // Format transcript
    const formattedTranscript = messages.map(msg => 
      `${msg.source === 'ai' ? 'AI Interviewer' : 'You'}: ${msg.message}`
    ).join('\n\n');
    
    // Create a blob with the formatted transcript
    const blob = new Blob([formattedTranscript], { type: 'text/plain' });
    
    // Create a download link and trigger download
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${practiceData.role}_Interview_Transcript.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success('Transcript downloaded');
  };

  // Start a new practice
  const handleStartNewPractice = () => {
    // Force a full page reload to reset all state
    window.location.href = '/practice-start';
  };

  return (
    <div className="w-full max-w-5xl mx-auto">
      {/* Interview interface */}
      {!isEnded ? (
        <div className="bg-white/90 rounded-lg border border-[#9b87f5]/30 shadow-lg w-full p-6 backdrop-blur-sm">
          {/* Header */}
          <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-[#1A1F2C]">
                {practiceData.role} Interview Practice
              </h1>
              <p className="text-[#1A1F2C]/70">
                Level: {practiceData.level.charAt(0).toUpperCase() + practiceData.level.slice(1)} | 
                Skills: {practiceData.selectedSkills.join(', ')}
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="text-white bg-red-500 border-red-500 hover:bg-red-600"
                onClick={endInterview}
                disabled={!hasStarted}
              >
                End Practice
              </Button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left side: AI conversation */}
            <div className="lg:col-span-7 bg-[#F8F7FF] rounded-lg p-6 border border-[#9b87f5]/20 flex flex-col min-h-[400px]">
              <div className="mb-4 flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${isSpeaking ? 'bg-green-500 animate-pulse' : 'bg-[#9b87f5]'}`} />
                <span className="font-medium text-[#1A1F2C]">AI Interviewer</span>
                {isSpeaking && <span className="text-xs text-green-500">Speaking</span>}
              </div>
              
              <div className="flex-1 flex items-center justify-center">
                {!hasStarted ? (
                  <div className="text-center">
                    {isConnecting ? (
                      <div className="flex flex-col items-center">
                        <div className="w-16 h-16 rounded-full bg-[#9b87f5]/10 flex items-center justify-center mb-4">
                          <div className="w-8 h-8 rounded-full border-4 border-[#9b87f5] border-t-transparent animate-spin" />
                        </div>
                        <p className="text-[#1A1F2C]/70">Connecting to interviewer...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <Button
                          size="lg"
                          className="bg-gradient-to-r from-[#9b87f5] to-[#7662DA] hover:from-[#8A76E5] hover:to-[#6552CA] mb-4 text-white"
                          onClick={startInterview}
                        >
                          <Mic className="mr-2" size={18} />
                          Start Practice Interview
                        </Button>
                        <p className="text-sm text-[#1A1F2C]/70">
                          Make sure your microphone is working and ready
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="w-full">
                    <div className="relative">
                      <div className="p-4 rounded-lg bg-white border border-[#9b87f5]/20">
                        {isQuestionTyping ? (
                          <>
                            <p className="text-[#1A1F2C]">{animatedQuestion}</p>
                            <span className="inline-block w-2 h-4 bg-[#9b87f5] animate-blink ml-1"></span>
                          </>
                        ) : (
                          <p className="text-[#1A1F2C]">{currentQuestion}</p>
                        )}
                      </div>
                      {isSpeaking && (
                        <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 flex space-x-1">
                          <span className="w-2 h-2 bg-[#9b87f5] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-2 h-2 bg-[#9b87f5] rounded-full animate-bounce" style={{ animationDelay: '250ms' }}></span>
                          <span className="w-2 h-2 bg-[#9b87f5] rounded-full animate-bounce" style={{ animationDelay: '500ms' }}></span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Right side: Transcript */}
            <div className="lg:col-span-5">
              <div className="bg-white rounded-lg border border-gray-200 h-full flex flex-col">
                <div className="p-3 border-b border-gray-200">
                  <h2 className="font-semibold">Conversation Transcript</h2>
                </div>
                
                <div className="flex-1 overflow-y-auto p-4 transcript-container" style={{ maxHeight: '400px' }}>
                  {messages.length === 0 ? (
                    <p className="text-sm text-gray-400">The conversation transcript will appear here...</p>
                  ) : (
                    messages.map((msg, i) => (
                      <div key={i} className="mb-3">
                        <div className={`flex ${msg.source === 'ai' ? 'justify-start' : 'justify-end'}`}>
                          <div className={`max-w-[90%] p-3 rounded-lg ${
                            msg.source === 'ai' 
                              ? 'bg-[#F8F7FF] text-[#1A1F2C] border border-[#9b87f5]/20' 
                              : 'bg-[#1A1F2C] text-white'
                          }`}>
                            <p className="text-xs font-medium mb-1">{msg.source === 'ai' ? 'AI Interviewer' : 'You'}</p>
                            <p className="text-sm">{msg.message}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Tips */}
          <div className="mt-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h3 className="font-medium text-blue-800 mb-2 flex items-center">
              <Brain className="mr-2" size={16} />
              Interview Tips
            </h3>
            <ul className="text-sm text-blue-700 space-y-1 pl-6 list-disc">
              <li>Speak clearly and at a moderate pace</li>
              <li>Provide specific examples from your experience</li>
              <li>Structure your answers with context, action, and results</li>
              <li>It's okay to take a moment to gather your thoughts</li>
              <li>Ask clarifying questions if needed</li>
            </ul>
          </div>
        </div>
      ) : (
        /* Practice completed screen */
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white/90 rounded-lg border border-[#9b87f5]/30 shadow-lg w-full p-8 backdrop-blur-sm text-center"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="text-green-500" size={40} />
          </div>
          
          <h1 className="text-3xl font-bold text-[#1A1F2C] mb-2">Practice Interview Completed</h1>
          <p className="text-[#1A1F2C]/70 mb-6 max-w-2xl mx-auto">
            Great job completing your {practiceData.role} practice interview! This experience will help you prepare
            for your real interviews.
          </p>
          
          {/* Recording section */}
          <div className="mb-6 p-4 bg-[#F8F7FF]/60 rounded-lg border border-[#9b87f5]/20 max-w-xl mx-auto">
            <h3 className="font-semibold text-[#1A1F2C] mb-3">Interview Recording</h3>
            
            {isDownloadingAudio ? (
              <div className="flex items-center justify-center p-6">
                <div className="w-8 h-8 rounded-full border-4 border-[#9b87f5] border-t-transparent animate-spin mr-3" />
                <span className="text-[#1A1F2C]/70">Retrieving recording...</span>
              </div>
            ) : audioUrl ? (
              <div className="space-y-4">
                <audio 
                  src={audioUrl} 
                  controls 
                  className="w-full rounded border border-[#9b87f5]/20"
                />
                
                <div className="flex justify-center">
                  <a 
                    href={audioUrl} 
                    download={`${practiceData.role}_Interview_Recording.mp3`}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#9b87f5] text-white hover:bg-[#8A76E5] transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-download">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Download Recording
                  </a>
                </div>
              </div>
            ) : conversationId ? (
              <div className="text-center py-4">
                <Button
                  onClick={fetchConversationAudio}
                  variant="outline"
                  className="bg-white text-[#9b87f5] border-[#9b87f5]/30 hover:bg-[#F8F7FF]"
                >
                  Retrieve Recording
                </Button>
              </div>
            ) : (
              <p className="text-center text-[#1A1F2C]/70 py-2">
                Recording not available. 
              </p>
            )}
          </div>
          
          {showLoginPrompt ? (
            <div className="mb-8 bg-amber-50 border border-amber-200 rounded-lg p-6 max-w-xl mx-auto">
              <div className="flex items-start">
                <AlertTriangle className="text-amber-500 mr-4 flex-shrink-0 mt-1" size={24} />
                <div className="text-left">
                  <h3 className="font-medium text-amber-800 mb-2">Save your practice interview</h3>
                  <p className="text-amber-700 mb-4">
                    To save this practice interview and review it later, please login or create an account.
                    This will help you track your progress over time.
                  </p>
                  <div className="flex gap-3">
                    <Button 
                      variant="outline"
                      className="border-amber-300 text-amber-800 hover:bg-amber-100 bg-white"
                      onClick={handleStartNewPractice}
                    >
                      No Thanks
                    </Button>
                    <Button 
                      className="bg-amber-500 hover:bg-amber-600 text-white opacity-70 cursor-not-allowed"
                      disabled={true}
                      onClick={(e) => {
                        e.preventDefault();
                        toast.info("This feature is coming soon!");
                      }}
                    >
                      Login & Save (Coming Soon)
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
          
          <div className="mb-8 bg-[#F8F7FF] rounded-lg p-6 border border-[#9b87f5]/20 max-h-[400px] overflow-y-auto text-left">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-semibold">Interview Transcript</h2>
              <Button
                onClick={downloadTranscript}
                variant="outline"
                size="sm"
                className="text-[#1A1F2C] bg-white border-[#9b87f5]/30 hover:bg-[#F8F7FF] flex items-center gap-1"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-download">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Download
              </Button>
            </div>
            {messages.map((msg, i) => (
              <div key={i} className="mb-4">
                <p className="font-medium text-sm text-[#1A1F2C]/70 mb-1">
                  {msg.source === 'ai' ? 'AI Interviewer' : 'You'}:
                </p>
                <p className="text-[#1A1F2C]">{msg.message}</p>
              </div>
            ))}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="outline"
              className="text-[#1A1F2C] bg-white border-[#9b87f5]/30 hover:bg-[#F8F7FF]"
              onClick={handleStartNewPractice}
            >
              Start New Practice Interview
            </Button>
            
            {!user && (
              <Button
                className="bg-gradient-to-r from-[#9b87f5] to-[#7662DA] hover:from-[#8A76E5] hover:to-[#6552CA] text-white opacity-70 cursor-not-allowed"
                disabled={true}
                onClick={(e) => {
                  e.preventDefault();
                  toast.info("This feature is coming soon!");
                }}
              >
                Login & Save Interview (Coming Soon)
              </Button>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default PracticeInterview; 